import type { TFunction } from 'i18next';
import { z } from 'zod';

/** Backend caps a deal gallery at 5 ordered image URLs. */
export const MAX_DEAL_MEDIA = 5;
/** Backend caps each media URL at 1024 characters. */
export const MAX_DEAL_MEDIA_URL_LENGTH = 1024;

/**
 * One editable gallery row. `useFieldArray` needs array-of-object entries, so
 * each URL is wrapped in an object rather than stored as a bare string.
 */
export interface DealMediaRow {
  url: string;
}

interface DealMediaSource {
  media?: string[] | null;
  image_url?: string | null;
}

/** Returns true when `value` parses as a URL (used for per-row validation). */
export function isValidMediaUrl(value: string): boolean {
  return z.string().url().safeParse(value).success;
}

/**
 * Collapse a deal's stored gallery into ordered, non-empty URLs. Prefers the
 * new `media` array and falls back to the legacy single `image_url` cover for
 * deals created before the gallery existed.
 */
function dealMediaUrls(deal?: DealMediaSource): string[] {
  if (!deal) return [];

  const media = deal.media;
  if (Array.isArray(media) && media.length > 0) {
    return media
      .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
      .map((url) => url.trim());
  }

  const cover = deal.image_url?.trim();
  return cover ? [cover] : [];
}

/**
 * Prefill gallery rows for the editor. Falls back to the legacy `image_url`
 * cover, and always seeds a single empty row when there is nothing so the form
 * shows one input to type into.
 */
export function dealMediaToRows(deal?: DealMediaSource): DealMediaRow[] {
  const urls = dealMediaUrls(deal);
  if (urls.length === 0) {
    return [{ url: '' }];
  }
  return urls.map((url) => ({ url }));
}

/**
 * Map editor rows to the ordered `media` payload: trim each URL and drop empty
 * rows. Order is preserved, so the first surviving URL is the cover the backend
 * derives `image_url` from.
 */
export function rowsToMediaPayload(rows: DealMediaRow[]): string[] {
  return rows.map((row) => row.url.trim()).filter((url) => url.length > 0);
}

/**
 * Zod sub-schema for the gallery field. Empty rows are allowed (they are
 * dropped on submit); non-empty rows must be valid URLs within the length cap;
 * the whole array is capped at {@link MAX_DEAL_MEDIA} entries.
 */
export function buildDealMediaSchema(t: TFunction) {
  return z
    .array(
      z.object({
        url: z
          .string()
          .max(MAX_DEAL_MEDIA_URL_LENGTH, t('deals.editor.media.err_url_long'))
          .refine(
            (value) => {
              const trimmed = value.trim();
              return trimmed === '' || isValidMediaUrl(trimmed);
            },
            { message: t('deals.editor.media.err_url') },
          ),
      }),
    )
    .max(MAX_DEAL_MEDIA, t('deals.editor.media.err_max', { max: MAX_DEAL_MEDIA }));
}
